/**
 * Booking confirmation status — single source of truth for the FRONTEND,
 * mirroring backend services/coverage.py::booking_display_status.
 *
 * Booking a worker and a worker CONFIRMING are two separate actions. Nothing
 * should read "Confirmed" until the talent personally confirmed (is_confirmed
 * / confirmation_status) or an admin used Admin Confirm Override.
 *
 * Accepts a booking/roster row with any of these fields (naming varies by API):
 *   booking_status | status, confirmation_status, is_confirmed,
 *   confirmation_source, assignment_type, is_standby, cancelled_by
 */
export const BOOKING_STATUS = {
  awaiting:        { label: 'Booked — Awaiting Confirmation', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  backupAwaiting:  { label: 'Backup — Awaiting Confirmation', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  confirmed:       { label: 'Confirmed by Talent',            cls: 'bg-green-100 text-green-800 border-green-300' },
  backupConfirmed: { label: 'Backup Confirmed',               cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  adminOverride:   { label: 'Confirmed by Admin (override)',  cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  legacyUnverified:{ label: 'Legacy — Not Talent Verified',   cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  reconfirmRequired:{ label: 'Reconfirmation Required',       cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  declined:        { label: 'Declined by Talent',             cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  released:        { label: 'Released by Talent',             cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  canceled:        { label: 'Canceled by Admin/Client',       cls: 'bg-slate-200 text-slate-700 border-slate-300' },
  noShow:          { label: 'No Show',                        cls: 'bg-rose-200 text-rose-900 border-rose-400' },
};

export function getBookingStatus(b = {}) {
  const status = String(b.booking_status || b.status || '').toLowerCase();
  const conf = String(b.confirmation_status || '').toLowerCase();
  const src = String(b.confirmation_source || '').toLowerCase();
  const tier = String(b.confirmation_tier || '').toLowerCase();
  const atype = String(b.assignment_type || 'primary').toLowerCase();
  const isBackup = !!b.is_standby || !['primary', 'direct'].includes(atype);
  const awaitingFlag = b.awaiting_confirmation === true;

  if (status === 'cancelled') {
    return String(b.cancelled_by || '').toLowerCase() === 'talent'
      ? BOOKING_STATUS.released : BOOKING_STATUS.canceled;
  }
  if (status === 'no_show') return BOOKING_STATUS.noShow;
  if (conf === 'declined') return BOOKING_STATUS.declined;

  // Confirmation-Integrity (2026-09-14): the green "Confirmed by Talent" badge
  // is driven by confirmation_tier, which ONLY a real talent confirm action
  // sets. Booking/admin/import/automated processes never produce it.
  if (tier === 'reconfirmation_required') return BOOKING_STATUS.reconfirmRequired;
  if (tier === 'talent_verified') {
    return isBackup ? BOOKING_STATUS.backupConfirmed : BOOKING_STATUS.confirmed;
  }
  if (tier === 'legacy_unverified') {
    return src === 'admin_override' ? BOOKING_STATUS.adminOverride : BOOKING_STATUS.legacyUnverified;
  }

  // No tier yet. If the row is flagged confirmed by a non-talent path, show it
  // as legacy/unverified (never green) rather than implying talent confirmation.
  const flaggedConfirmed = !awaitingFlag && (
    !!b.is_confirmed || !!b.confirmed_at || ['confirmed', 'day_confirmed'].includes(conf)
  );
  if (flaggedConfirmed) {
    if (src === 'admin_override') return BOOKING_STATUS.adminOverride;
    if (['talent_portal', 'talent_app', 'quick_confirm_email', 'quick_confirm_sms'].includes(src)) {
      return isBackup ? BOOKING_STATUS.backupConfirmed : BOOKING_STATUS.confirmed;
    }
    return BOOKING_STATUS.legacyUnverified;
  }
  return isBackup ? BOOKING_STATUS.backupAwaiting : BOOKING_STATUS.awaiting;
}
